import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';

export type ColorMode = 'light' | 'dark';

const STORAGE_KEY = 'flux:color-mode';
const DARK_ATTR = 'data-theme';

/**
 * Aplica e persiste o color mode (light / dark).
 *
 * O atributo `data-theme="dark"` é setado no `<html>` quando o modo é dark.
 * O design-system reage automaticamente — todos os tokens semânticos
 * (`--palantir-color-*`) trocam de valor (definidos em
 * `:root[data-theme='dark']` no design-system).
 *
 * Persistência: `localStorage` (chave `flux:color-mode`).
 *
 * Uso:
 * ```ts
 * private colorMode = inject(ColorModeService);
 * // ...
 * this.colorMode.toggle();
 * // ou
 * this.colorMode.set('dark');
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ColorModeService {
  private readonly document = inject(DOCUMENT);
  private readonly storage =
    typeof globalThis !== 'undefined' && 'localStorage' in globalThis
      ? globalThis.localStorage
      : null;

  /** Estado reativo (signal). Use em templates: `{{ colorMode.mode() }}`. */
  public readonly mode = signal<ColorMode>(this.readInitial());

  constructor() {
    this.apply(this.mode());
  }

  /** Alterna entre light e dark, persiste e aplica. */
  public toggle(): void {
    this.set(this.mode() === 'dark' ? 'light' : 'dark');
  }

  /** Define o modo, persiste e aplica. */
  public set(mode: ColorMode): void {
    this.mode.set(mode);
    this.apply(mode);
    this.persist(mode);
  }

  private readInitial(): ColorMode {
    const stored = this.storage?.getItem(STORAGE_KEY) as ColorMode | null;
    if (stored === 'dark' || stored === 'light') return stored;
    // Default = light. (Para honrar preferência do SO,
    // troque por `prefers-color-scheme` aqui.)
    return 'light';
  }

  private apply(mode: ColorMode): void {
    const html = this.document.documentElement;
    if (mode === 'dark') html.setAttribute(DARK_ATTR, 'dark');
    else html.removeAttribute(DARK_ATTR);
  }

  private persist(mode: ColorMode): void {
    this.storage?.setItem(STORAGE_KEY, mode);
  }
}
